import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { classifyDocument as patternClassify } from '../../../lib/documents/classifier';
import { PDFParse } from 'pdf-parse';

/**
 * POST /api/documents/auto-classify
 *
 * Auto-classifies documents using AI with pattern-based fallback.
 *
 * Body options:
 *   { document_id: string }      → Classify a single document
 *   { document_ids: string[] }   → Classify multiple documents
 *
 * Returns: { success, results: Array<{ document_id, tipo, confidence }> }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const { document_id, document_ids } = req.body;

    // Normalize to array of IDs
    let docIds: string[] = [];

    if (document_ids && Array.isArray(document_ids) && document_ids.length > 0) {
      docIds = document_ids;
    } else if (document_id && typeof document_id === 'string') {
      docIds = [document_id];
    } else {
      return res.status(400).json({
        success: false,
        error: 'Se requiere document_id o document_ids.',
      });
    }

    // Fetch full document records from 'documentos' table
    const { data: docs, error: docsError } = await supabaseAdmin
      .from('documentos')
      .select('id, nombre, url_archivo, tipo')
      .in('id', docIds);

    if (docsError) {
      return res.status(500).json({
        success: false,
        error: `Error cargando documentos: ${docsError.message}`,
      });
    }

    if (!docs || docs.length === 0) {
      return res.status(404).json({
        success: false,
        error: 'No se encontraron documentos con los IDs proporcionados.',
      });
    }

    const results: Array<{ document_id: string; tipo: string; confidence: number }> = [];

    for (const doc of docs) {
      try {
        // Download file from storage to extract text
        let extractedText = '';

        if (doc.url_archivo) {
          // Extract storage path from the public URL
          const storagePath = doc.url_archivo.replace(
            /^https:\/\/[^/]+\/storage\/v1\/object\/public\/documentos\//,
            ''
          );

          if (storagePath && storagePath !== doc.url_archivo) {
            const { data: fileData, error: downloadError } = await supabaseAdmin.storage
              .from('documentos')
              .download(storagePath);

            if (!downloadError && fileData) {
              const buffer = Buffer.from(await fileData.arrayBuffer());

              // Try to extract text if it's a PDF
              if (doc.nombre?.toLowerCase().endsWith('.pdf')) {
                try {
                  const pdf = new PDFParse({ data: buffer });
                  const textResult = await pdf.getText();
                  extractedText = textResult.text || '';
                  await pdf.destroy();
                } catch {
                }
              }
            }
          }
        }

        let tipo: string;
        let confidence: number;

        try {
          const { classifyWithAI } = await import('../../../lib/documents/ai-classifier');
          const aiResult = await classifyWithAI(
            extractedText,
            doc.nombre || 'documento'
          );
          tipo = aiResult.tipo;
          confidence = aiResult.confidence;
        } catch {
          // Fallback to pattern classifier
          const patternResult = patternClassify(
            doc.nombre || 'documento',
            undefined,
            extractedText
          );
          tipo = patternResult.tipo;
          confidence = patternResult.confidence;
        }

        // Update document with classified tipo
        const { error: updateError } = await supabaseAdmin
          .from('documentos')
          .update({ tipo })
          .eq('id', doc.id);

        if (updateError) {
          console.error(`[auto-classify] Error updating doc ${doc.id}:`, updateError.message);
        }

        results.push({
          document_id: doc.id,
          tipo,
          confidence,
        });
      } catch (docErr: unknown) {
        const msg = docErr instanceof Error ? docErr.message : 'Error desconocido';
        console.error(`[auto-classify] Error classifying doc ${doc.id}:`, msg);
        results.push({
          document_id: doc.id,
          tipo: doc.tipo || 'otro',
          confidence: 0,
        });
      }
    }

    return res.status(200).json({
      success: true,
      results,
    });
  } catch (error: any) {
    console.error('[auto-classify] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al clasificar documentos.',
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
